import React from "react";
import { connect } from "react-redux";

import { IAppState } from "../../state/Store";
import RichText from "../inputform/RichText";
import Utility from "../../foundation/Utility";
import { addAlert } from "../../state/system/Alerts";
import AlertEntry from "../../types/system/AlertEntry";

interface MessagingEditorProps {
    body: string;
    isSystem: boolean;
    broadcasting: boolean;
    disableSend: boolean;
    sending: boolean;
    addAlert: (message: AlertEntry) => void;
    onSend: (broadcast: boolean) => void;
    onChange: (html: string) => void;
}

interface MessagingEditorState {
    contRef: React.RefObject<HTMLDivElement>;
    editRef: React.RefObject<RichText>;
    isDragged: boolean;
}

class MessagingEditor extends React.Component<
    MessagingEditorProps,
    MessagingEditorState
> {
    state = {
        contRef: React.createRef<HTMLDivElement>(),
        editRef: React.createRef<RichText>(),
        isDragged: false,
    };

    componentDidMount(): void {
        document.addEventListener("dragenter", this.dragEnter.bind(this), true);
        document.addEventListener("dragover", this.dragEnter.bind(this), true);
        document.addEventListener("dragleave", this.dragLeave.bind(this), true);
        document.addEventListener("drop", this.dragLeave.bind(this));
    }

    componentDidUpdate(
        prevProps: Readonly<MessagingEditorProps>,
        prevState: Readonly<MessagingEditorState>,
        snapshot?: any,
    ): void {
        if (
            prevProps.broadcasting !== this.props.broadcasting &&
            this.props.broadcasting
        ) {
            const delta = this.state.editRef.current.state.ref.current.editor
                .clipboard.convert(`
                        <p style="text-align: center;">
                            <span class="ql-size-huge">
                                Message Title
                            </span>
                        </p>
                        <br>
                        <p>
                            Message text...
                        </p>
                        `);

            this.state.editRef.current.state.ref.current.editor.setContents(
                delta,
            );
        }
    }

    hasImage() {
        return (
            this.props.body.indexOf('<p><img src="data:image/jpeg;base64') >= 0
        );
    }

    hasTemplate(): boolean {
        return this.props.body.indexOf("template-container") >= 0;
    }

    onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        if (
            event.key === "Enter" &&
            !event.altKey &&
            !this.props.broadcasting
        ) {
            if (!this.props.disableSend) {
                this.props.onSend(false);
            }
            event.preventDefault();
        } else if (this.hasImage()) {
            if (event.key === "Delete" || event.key === "Backspace") {
                this.state.editRef.current.state.ref.current.editor.setText("");
            } else {
                event.preventDefault();
            }
        }
    }

    dragEnter(event: DragEvent) {
        if (event.dataTransfer?.types.includes("Files")) {
            this.setState({ isDragged: true });
            event.dataTransfer.dropEffect = "copy";
        }
        event.preventDefault();
    }

    dragLeave(event: DragEvent) {
        this.setState({ isDragged: false });
        event.preventDefault();
    }

    async dragDrop(event: DragEvent) {
        if (!this.props.disableSend) {
            event.preventDefault();
            event.stopPropagation();

            for (
                let index = 0;
                index < (event.dataTransfer?.files.length ?? 0);
                index++
            ) {
                let file = event.dataTransfer?.files[index];
                if (file?.type.indexOf("image/") === 0) {
                    const fileData = await Utility.resizeImage(
                        (await Utility.fileToBase64(file)).toDataUrl(),
                        200,
                    );
                    this.state.editRef.current.state.ref.current.editor.setText(
                        "",
                    );
                    this.state.editRef.current.state.ref.current.editor.insertEmbed(
                        0,
                        "image",
                        fileData,
                        // this.props.broadcasting ? 550 : 300
                    );
                } else {
                    this.props.addAlert({
                        title: "Error",
                        icon: "error",
                        styleColor: "red",
                        text: "This file type is currently unsupported",
                    });
                }
            }

            this.setState({ isDragged: false });
        }
    }

    render(): React.ReactNode {
        return (
            !this.props.isSystem && (
                <div
                    ref={this.state.contRef}
                    className={`flex message-input ${
                        this.props.broadcasting ? "col" : "col-pane"
                    }${this.state.isDragged ? " dragdrop" : ""}${
                        this.hasImage() ? " image-edit" : ""
                    }`}
                    onDrop={this.dragDrop.bind(this)}>
                    <div className="col">
                        <RichText
                            ref={this.state.editRef}
                            value={this.props.body}
                            placeholder="Message..."
                            disabled={this.props.disableSend}
                            messenger={!this.props.broadcasting}
                            onKeyDown={this.onKeyDown.bind(this)}
                            onChange={this.props.onChange}></RichText>
                    </div>
                    <div className="col-pane">
                        {!this.props.broadcasting && (
                            <button
                                className="theme-button message-send-button"
                                disabled={this.props.disableSend}
                                onClick={() => this.props.onSend(false)}>
                                {this.props.sending ? (
                                    <i className="spin">sync</i>
                                ) : (
                                    <i className="fill">send</i>
                                )}
                                <span>Send</span>
                            </button>
                        )}
                    </div>
                </div>
            )
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    return {};
};

const mapDispatchToProps = dispatch => {
    return {
        addAlert: (message: AlertEntry) => dispatch(addAlert(message)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MessagingEditor);
